import React from "react";
import { Link } from "react-router-dom";
import { ReactComponent as ChevronDownSvg } from "../../../assets/Icons/IconChevron-Down.svg";
import { MenuItem as MenuItemType } from "../MenuData";

interface MenuItemProps {
  item: MenuItemType;
  expandedSections: { [key: string]: boolean };
  setExpandedSections: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  activeItem: string;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  onClick: (item: MenuItemType) => void;
  isExpanded?: boolean;
}

function isActiveOrChildActive(
  item: MenuItemType,
  activeItem: string
): boolean {
  if (item.label === activeItem) return true;
  if (item.children) {
    for (const child of item.children) {
      if (isActiveOrChildActive(child, activeItem)) {
        return true;
      }
    }
  }
  return false;
}

const MenuItem: React.FC<MenuItemProps> = ({
  item,
  expandedSections,
  setExpandedSections,
  activeItem,
  setActiveItem,
  onClick,
  isExpanded = true,
}) => {
  const isParent = !!item.children;
  const isSectionExpanded = expandedSections[item.label] || false;
  const activeOrChildActive = isActiveOrChildActive(item, activeItem);
  const isSelfActive = item.label === activeItem;
  const isSomeChildActive = !isSelfActive && activeOrChildActive;

  const handleItemClick = (e: React.MouseEvent) => {
    if (isParent) {
      e.preventDefault();
      setExpandedSections((prev) => ({
        ...prev,
        [item.label]: !isSectionExpanded,
      }));
      setActiveItem(item.label);
    } else {
      setActiveItem(item.label);
      onClick(item);
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedSections((prev) => ({
      ...prev,
      [item.label]: !isSectionExpanded,
    }));
    setActiveItem(item.label);
  };

  return (
    <li className="text-sm">
      <div className="flex justify-between items-center">
        {/* 
          Si es padre, el link NO navega (o apunta a "#"). 
          Si no es padre (no tiene children), usamos item.path para navegar. 
        */}
        <Link
          to={isParent ? "#" : item.path || "#"}
          onClick={handleItemClick}
          className={`
            block w-full py-2 px-3 rounded-sm
            ${
              isSelfActive
                ? "bg-semantic-state-active text-semantic-text-brand font-semibold"
                : isSomeChildActive
                ? "text-semantic-text-brand font-medium"
                : "hover:bg-semantic-state-active text-semantic-text-secondary"
            }
          `}
        >
          <div className="flex items-center gap-2">
            {item.Icon && <item.Icon className="h-5 w-5" />}
            {isExpanded && (
              <span className="flex items-center justify-between">
                {item.label}{" "}
                {item.badge && (
                  <span className="ml-2 text-[10px] font-bold uppercase bg-emerald-100 text-semantic-text-brand px-1 py-[2px] rounded">
                    {item.badge}
                  </span>
                )}
              </span>
            )}
          </div>
        </Link>

        {/* 
          Botón flecha para expandir/cerrar
          Aparece sólo si es padre y el menú está expandido 
        */}
        {isParent && isExpanded && (
          <button
            onClick={handleChevronClick}
            className="px-2 focus:outline-none"
          >
            <ChevronDownSvg
              className={`h-4 w-4 transition-transform ${
                isSectionExpanded ? "rotate-180" : ""
              }`}
            />
          </button>
        )}
      </div>

      {/* Submenú */}
      {isExpanded && isSectionExpanded && item.children && (
        <ul className="ml-4">
          {item.children.map((child) => (
            <MenuItem
              key={child.label}
              item={child}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              onClick={onClick}
              isExpanded={isExpanded}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default MenuItem;
